import { StrictMode, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  CheckCircle2,
  Clipboard,
  Code2,
  Database,
  Download,
  FileKey2,
  Filter,
  FolderLock,
  Github,
  Globe2,
  KeyRound,
  Laptop,
  LockKeyhole,
  Network,
  Search,
  ServerCog,
  ShieldCheck,
  SlidersHorizontal,
  TerminalSquare,
  XCircle
} from "lucide-react";
import "./styles.css";

type Risk = "low" | "medium" | "high";
type Category = "All" | "Files" | "Dev Tools" | "Databases" | "Search" | "Productivity" | "Browser";
type Client = "Claude Desktop" | "Cursor" | "Codex" | "VS Code" | "Generic";

type ServerEntry = {
  id: string;
  name: string;
  category: Exclude<Category, "All">;
  description: string;
  command: string;
  args: string[];
  env: Record<string, string>;
  transport: "stdio" | "sse" | "http";
  risk: Risk;
  source: string;
  reviewed: string;
  permissions: string[];
};

const catalog: ServerEntry[] = [
  {
    id: "filesystem",
    name: "Filesystem",
    category: "Files",
    description: "Read and optionally write local project files with allowlisted paths.",
    command: "npx",
    args: ["-y", "@modelcontextprotocol/server-filesystem", "$PROJECT_PATH"],
    env: { PROJECT_PATH: "/absolute/path/to/project", READ_ONLY: "false" },
    transport: "stdio",
    risk: "medium",
    source: "https://github.com/modelcontextprotocol/servers",
    reviewed: "2026-06-14",
    permissions: ["local-read", "local-write"]
  },
  {
    id: "github",
    name: "GitHub",
    category: "Dev Tools",
    description: "Work with repositories, issues, pull requests, commits, and code search.",
    command: "npx",
    args: ["-y", "@modelcontextprotocol/server-github"],
    env: { GITHUB_PERSONAL_ACCESS_TOKEN: "${GITHUB_TOKEN}" },
    transport: "stdio",
    risk: "medium",
    source: "https://github.com/modelcontextprotocol/servers",
    reviewed: "2026-06-14",
    permissions: ["network", "oauth-token", "repo-write"]
  },
  {
    id: "postgres",
    name: "Postgres",
    category: "Databases",
    description: "Query a PostgreSQL database with connection-string scoped access.",
    command: "npx",
    args: ["-y", "mcp-postgres"],
    env: { DATABASE_URL: "${POSTGRES_READONLY_URL}" },
    transport: "stdio",
    risk: "medium",
    source: "https://github.com/punkpeye/awesome-mcp-servers",
    reviewed: "2026-06-14",
    permissions: ["network", "database", "env-secret"]
  },
  {
    id: "fetch",
    name: "Fetch",
    category: "Search",
    description: "Fetch public web pages and return text for research and browsing workflows.",
    command: "npx",
    args: ["-y", "@modelcontextprotocol/server-fetch"],
    env: {},
    transport: "stdio",
    risk: "low",
    source: "https://github.com/modelcontextprotocol/servers",
    reviewed: "2026-06-14",
    permissions: ["network"]
  },
  {
    id: "playwright",
    name: "Playwright",
    category: "Browser",
    description: "Drive a local browser for web QA, screenshots, and automation tasks.",
    command: "npx",
    args: ["-y", "@playwright/mcp"],
    env: {},
    transport: "stdio",
    risk: "high",
    source: "https://github.com/punkpeye/awesome-mcp-servers",
    reviewed: "2026-06-14",
    permissions: ["browser-control", "network", "local-write"]
  },
  {
    id: "sqlite",
    name: "SQLite",
    category: "Databases",
    description: "Inspect local SQLite databases and run scoped queries.",
    command: "npx",
    args: ["-y", "mcp-server-sqlite", "$DATABASE_PATH"],
    env: { DATABASE_PATH: "/absolute/path/app.db", READ_ONLY: "true" },
    transport: "stdio",
    risk: "medium",
    source: "https://github.com/punkpeye/awesome-mcp-servers",
    reviewed: "2026-06-14",
    permissions: ["local-read", "database"]
  },
  {
    id: "memory",
    name: "Memory",
    category: "Productivity",
    description: "Store and retrieve local notes or graph memory for an assistant.",
    command: "npx",
    args: ["-y", "@modelcontextprotocol/server-memory"],
    env: {},
    transport: "stdio",
    risk: "low",
    source: "https://github.com/modelcontextprotocol/servers",
    reviewed: "2026-06-14",
    permissions: ["local-state"]
  },
  {
    id: "slack",
    name: "Slack",
    category: "Productivity",
    description: "Read channels and post messages through a Slack app token.",
    command: "npx",
    args: ["-y", "mcp-server-slack"],
    env: { SLACK_BOT_TOKEN: "${SLACK_BOT_TOKEN}", SLACK_TEAM_ID: "${SLACK_TEAM_ID}" },
    transport: "stdio",
    risk: "high",
    source: "https://github.com/punkpeye/awesome-mcp-servers",
    reviewed: "2026-06-14",
    permissions: ["network", "oauth-token", "external-write"]
  },
  {
    id: "time",
    name: "Time",
    category: "Productivity",
    description: "Return current time and timezone conversions for scheduling workflows.",
    command: "npx",
    args: ["-y", "mcp-server-time"],
    env: {},
    transport: "stdio",
    risk: "low",
    source: "https://github.com/punkpeye/awesome-mcp-servers",
    reviewed: "2026-06-14",
    permissions: []
  },
  {
    id: "docker",
    name: "Docker",
    category: "Dev Tools",
    description: "Manage containers, images, and development environments.",
    command: "npx",
    args: ["-y", "mcp-server-docker"],
    env: {},
    transport: "stdio",
    risk: "high",
    source: "https://github.com/punkpeye/awesome-mcp-servers",
    reviewed: "2026-06-14",
    permissions: ["command", "local-write", "network"]
  }
];

const categories: Category[] = ["All", "Files", "Dev Tools", "Databases", "Search", "Productivity", "Browser"];
const clients: Client[] = ["Claude Desktop", "Cursor", "Codex", "VS Code", "Generic"];

const permissionLabels: Record<string, { title: string; body: string; risk: Risk }> = {
  "local-read": { title: "Reads local files", body: "Limit paths to project folders or read-only fixtures.", risk: "medium" },
  "local-write": { title: "Writes local files", body: "Review every writable path and avoid broad home-directory access.", risk: "high" },
  "network": { title: "Uses network", body: "Server may send requests to external APIs or websites.", risk: "medium" },
  "oauth-token": { title: "Needs token", body: "Use environment variables. Do not paste secrets into generated config.", risk: "medium" },
  "repo-write": { title: "Repository mutation", body: "Scope GitHub tokens to the minimum repo permissions.", risk: "high" },
  "database": { title: "Database access", body: "Prefer read-only users and safe schemas for AI assistants.", risk: "medium" },
  "env-secret": { title: "Environment secret", body: "Secret values should live outside this page and outside source control.", risk: "medium" },
  "browser-control": { title: "Browser control", body: "Automation can click, type, and inspect authenticated pages.", risk: "high" },
  "local-state": { title: "Stores state", body: "Check where memories or notes are persisted.", risk: "low" },
  "external-write": { title: "External writes", body: "Assistant may post messages or update third-party systems.", risk: "high" },
  "command": { title: "Command execution", body: "Treat shell/container control as high risk.", risk: "high" }
};

function App() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<Category>("All");
  const [client, setClient] = useState<Client>("Claude Desktop");
  const [selected, setSelected] = useState<string[]>(["filesystem", "github", "fetch"]);
  const [copied, setCopied] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog.filter((server) => {
      const matchesCategory = category === "All" || server.category === category;
      const matchesQuery = !q || `${server.name} ${server.description} ${server.category}`.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [category, query]);

  const selectedServers = useMemo(() => catalog.filter((server) => selected.includes(server.id)), [selected]);
  const config = useMemo(() => buildConfig(selectedServers, client), [client, selectedServers]);
  const riskCounts = useMemo(() => countRisks(selectedServers), [selectedServers]);
  const permissions = useMemo(() => {
    const ids = [...new Set(selectedServers.flatMap((server) => server.permissions))];
    return ids.map((id) => ({ id, ...permissionLabels[id] })).filter((item) => item.title);
  }, [selectedServers]);

  async function copyConfig() {
    await navigator.clipboard.writeText(config);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1300);
  }

  function toggleServer(id: string) {
    setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <a data-google-vignette="false" className="brand" href="/">
          <span><ShieldCheck size={24} /></span>
          <div>
            <h1>MCPGuard Config</h1>
            <small>MCP server finder, config builder, and permission checklist</small>
          </div>
        </a>
        <nav aria-label="Primary">
          <a data-google-vignette="false" href="#catalog">Catalog</a>
          <a data-google-vignette="false" href="#config">Config</a>
          <a data-google-vignette="false" href="#audit">Risk Audit</a>
        </nav>
        <a data-google-vignette="false" className="utility-link" href="/privacy.html">Privacy</a>
      </header>

      <section className="overview-grid" aria-label="MCP configuration summary">
        <Metric label="Selected servers" value={String(selectedServers.length)} detail="No secret values collected" icon={<ServerCog size={20} />} />
        <div className="risk-summary">
          <span>Risk summary</span>
          <div>
            <RiskBox label="High" value={riskCounts.high} risk="high" />
            <RiskBox label="Medium" value={riskCounts.medium} risk="medium" />
            <RiskBox label="Low" value={riskCounts.low} risk="low" />
          </div>
        </div>
        <div className="client-panel">
          <span>Client target</span>
          <div className="client-tabs">
            {clients.map((item) => (
              <button key={item} className={client === item ? "active" : ""} onClick={() => setClient(item)} type="button">
                {item}
              </button>
            ))}
          </div>
        </div>
        <Metric label="Config format" value="JSON" detail="Claude/Cursor-compatible starter" icon={<Code2 size={20} />} />
      </section>

      <section className="workspace" id="catalog">
        <aside className="filter-rail">
          <div className="rail-title">
            <Filter size={18} />
            <strong>Discover</strong>
          </div>
          <div className="category-list">
            {categories.map((item) => (
              <button key={item} className={category === item ? "active" : ""} onClick={() => setCategory(item)} type="button">
                {item}
                <span>{item === "All" ? catalog.length : catalog.filter((server) => server.category === item).length}</span>
              </button>
            ))}
          </div>
          <div className="policy-note">
            <LockKeyhole size={18} />
            <p>Use placeholders for tokens. Store real secrets in your local environment or password manager.</p>
          </div>
        </aside>

        <section className="catalog-panel">
          <div className="catalog-toolbar">
            <label className="search-box">
              <Search size={18} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search MCP servers, e.g. filesystem, postgres, github"
              />
            </label>
            <button type="button"><SlidersHorizontal size={17} /> Filters</button>
          </div>
          <div className="server-table">
            <div className="table-head">
              <span>Server</span>
              <span>Description</span>
              <span>Risk</span>
              <span>Permissions</span>
              <span>Reviewed</span>
            </div>
            {filtered.map((server) => (
              <article key={server.id} className={`server-row ${selected.includes(server.id) ? "selected" : ""}`}>
                <label>
                  <input type="checkbox" checked={selected.includes(server.id)} onChange={() => toggleServer(server.id)} />
                  <span>{serverIcon(server.category)}</span>
                  <strong>{server.name}<small>{server.transport}</small></strong>
                </label>
                <p>{server.description}<a data-google-vignette="false" href={server.source} target="_blank" rel="noreferrer">Source</a></p>
                <RiskBadge risk={server.risk} />
                <div className="permission-chips">
                  {server.permissions.slice(0, 4).map((permission) => <span key={permission}>{permission.replace("-", " ")}</span>)}
                </div>
                <time>{server.reviewed}</time>
              </article>
            ))}
          </div>
        </section>

        <aside className="config-panel" id="config">
          <div className="panel-heading">
            <div>
              <span>Generated config</span>
              <h2>{client}</h2>
            </div>
            <button type="button" onClick={copyConfig}><Clipboard size={17} /> {copied ? "Copied" : "Copy"}</button>
          </div>
          <pre>{config}</pre>
          <div className="selected-stack">
            <span>Selected stack</span>
            {selectedServers.map((server) => (
              <button key={server.id} type="button" onClick={() => toggleServer(server.id)}>
                <XCircle size={15} />
                {server.name}
              </button>
            ))}
          </div>
        </aside>
      </section>

      <section className="audit-panel" id="audit">
        <div className="section-title">
          <span><AlertTriangle size={18} /> Permission checklist</span>
          <p>Review what the selected MCP servers can touch before you paste the config into a local AI client.</p>
        </div>
        <div className="audit-grid">
          {permissions.map((permission) => (
            <article key={permission.id} className={`audit-card ${permission.risk}`}>
              {permission.risk === "high" ? <AlertTriangle size={20} /> : permission.risk === "medium" ? <FileKey2 size={20} /> : <CheckCircle2 size={20} />}
              <div>
                <strong>{permission.title}</strong>
                <p>{permission.body}</p>
              </div>
              <RiskBadge risk={permission.risk} />
            </article>
          ))}
        </div>
      </section>

      <section className="seo-panel">
        <div>
          <Github size={20} />
          <h2>MCP config generator and server risk checklist</h2>
        </div>
        <p>
          Build Model Context Protocol config JSON for Claude Desktop, Cursor, Codex, VS Code, and other clients while
          checking local file, network, token, browser, database, and command permissions.
        </p>
        <button type="button" onClick={copyConfig}><Download size={17} /> Export config</button>
      </section>

      <footer>
        <span>Independent MCP setup helper. Verify every server against its official documentation before installing.</span>
        <nav>
          <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          <a data-google-vignette="false" href="/about.html">About</a>
        </nav>
      </footer>
    </main>
  );
}

function buildConfig(servers: ServerEntry[], client: Client) {
  const mcpServers = Object.fromEntries(servers.map((server) => [
    server.id,
    {
      command: server.command,
      args: server.args,
      ...(Object.keys(server.env).length ? { env: server.env } : {})
    }
  ]));
  const payload = client === "Generic" ? { mcpServers, notes: "Adapt this starter to your MCP client schema." } : { mcpServers };
  return JSON.stringify(payload, null, 2);
}

function countRisks(servers: ServerEntry[]) {
  return servers.reduce((counts, server) => ({ ...counts, [server.risk]: counts[server.risk] + 1 }), {
    high: 0,
    medium: 0,
    low: 0
  });
}

function RiskBox({ label, value, risk }: { label: string; value: number; risk: Risk }) {
  return (
    <article className={`risk-box ${risk}`}>
      <strong>{value}</strong>
      <span>{label}</span>
    </article>
  );
}

function RiskBadge({ risk }: { risk: Risk }) {
  return <span className={`risk-badge ${risk}`}>{risk}</span>;
}

function Metric({ icon, label, value, detail }: { icon: React.ReactNode; label: string; value: string; detail: string }) {
  return (
    <article className="metric">
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <p>{detail}</p>
      </div>
    </article>
  );
}

function serverIcon(category: ServerEntry["category"]) {
  if (category === "Files") return <FolderLock size={22} />;
  if (category === "Databases") return <Database size={22} />;
  if (category === "Search") return <Globe2 size={22} />;
  if (category === "Browser") return <Laptop size={22} />;
  if (category === "Dev Tools") return <TerminalSquare size={22} />;
  if (category === "Productivity") return <Network size={22} />;
  return <ServerCog size={22} />;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
